'use client'

import { useEffect } from 'react'
import { getEnvStage } from '@utils/environments'
import { log } from '@utils/logger'
import { importDatadogRum } from '@/utils/datadogRum'

// Module-level (not component-level) guard: React Strict Mode, Fast Refresh, and any future
// second mount of this component all share this flag, since `datadogRum.init()` is a call into a
// module-level SDK singleton. A `useRef` guard would not help here — each mount gets its own ref,
// so a remount would call `init()` again and trigger the SDK's own "already initialized" warning.
let hasInitStarted = false

/** Test-only: clears the module-level guard so each test starts from a clean state. */
export function resetDatadogInitGuard(): void {
  hasInitStarted = false
}

export default function DatadogInit() {
  useEffect(() => {
    if (hasInitStarted) {
      return
    }
    hasInitStarted = true

    const initDatadog = async () => {
      try {
        const { datadogRum } = await importDatadogRum()

        // Initialize RUM
        const applicationId = window.__env__?.ddApplicationId || 'unknown'
        const env = window.__env__?.stage || getEnvStage()
        const clientToken = window.__env__?.ddClientToken || 'unknown'
        const service = window.__env__?.ddService || 'unknown'

        datadogRum.init({
          applicationId,
          clientToken,
          site: 'datadoghq.com',
          service,
          env,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 0,
          defaultPrivacyLevel: 'mask-user-input',
          trackResources: true,
          trackLongTasks: true,
          trackUserInteractions: true,
        })
      } catch (error) {
        hasInitStarted = false
        log.warn('Failed to initialize Datadog RUM', {
          errorName: error instanceof Error ? error.name : 'UnknownError',
        })
      }
    }

    void initDatadog()
  }, [])

  // Render nothing - this component is only included so that the init code
  // above will run client-side
  return null
}
